/**
 * The only uploader in the app. A file goes straight from the browser to S3
 * with a presigned POST when the workspace stores files there; if that fails
 * for any transport reason (network, CORS, timeout, S3 error) it is sent
 * through the app instead, and the server moves it to S3 later. In local mode
 * it goes through the app directly. Either way the caller gets a FileRef.
 */
import { useSyncExternalStore } from 'react';
import type { FileRef, UploadLimits, UploadModule } from '@/api/files';
import { downloadFile } from '@/api/payroll-runs';
import { api, ApiError, apiUpload } from '@/lib/api-client';

const MB = 1024 * 1024;
/** A direct upload that makes no progress for this long is given up and re-sent through the app. */
const STALL_MS = 30_000;

export class UploadError extends Error {}

type InitResponse =
  | { mode: 'DEDUP'; file: FileRef }
  | { mode: 'S3'; upload_id: string; url: string; fields: Record<string, string> }
  | { mode: 'LOCAL'; upload_id: string };

let inFlight = 0;
const listeners = new Set<() => void>();
function setInFlight(delta: number) {
  inFlight += delta;
  listeners.forEach((l) => l());
}

/** How many uploads are running anywhere on the page — Save buttons wait for zero. */
export function useUploadsInFlight(): number {
  return useSyncExternalStore((l) => { listeners.add(l); return () => listeners.delete(l); }, () => inFlight);
}

export function extensionOf(name: string): string {
  const dot = name.lastIndexOf('.');
  return dot < 0 ? '' : name.slice(dot + 1).toLowerCase();
}

/** The reason a file cannot be uploaded under these limits, checked before any byte is sent. */
export function refusal(file: File, limits: UploadLimits, maxBytes = limits.max_file_bytes): string | undefined {
  if (!limits.can_upload) return limits.reason ?? 'Uploads are not allowed here.';
  const ext = extensionOf(file.name);
  if (!limits.allowed_extensions.includes(ext)) {
    return `${(ext || 'This').toUpperCase()} files are not allowed. Allowed: ${limits.allowed_extensions.join(', ').toUpperCase()}.`;
  }
  if (file.size === 0) return 'The file is empty.';
  if (file.size > maxBytes) return `Files here can be at most ${Math.max(1, Math.round(maxBytes / MB))} MB.`;
  return undefined;
}

/** Phone photos are often several MB; scale large JPEGs down when the workspace asks for it. */
async function compress(file: File, cfg: UploadLimits['image_compression']): Promise<File> {
  if (!cfg.enabled || file.type !== 'image/jpeg' || file.size < cfg.min_bytes || !('createImageBitmap' in window)) {
    return file;
  }
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, cfg.max_dimension_px / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(bitmap.height * scale);
  canvas.getContext('2d')?.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close();
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', cfg.jpeg_quality));
  return blob && blob.size < file.size ? new File([blob], file.name, { type: 'image/jpeg' }) : file;
}

async function sha256Hex(file: File): Promise<string | undefined> {
  // SubtleCrypto exists only in secure contexts (https or localhost); without it dedup is simply skipped.
  if (!crypto?.subtle) return undefined;
  const digest = await crypto.subtle.digest('SHA-256', await file.arrayBuffer());
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

function postToS3(url: string, fields: Record<string, string>, file: File,
                  onProgress?: (pct: number | null) => void, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const form = new FormData();
    Object.entries(fields).forEach(([k, v]) => form.append(k, v));
    form.append('file', file);
    let stall = 0;
    const arm = () => { window.clearTimeout(stall); stall = window.setTimeout(() => xhr.abort(), STALL_MS); };
    const done = (fn: () => void) => () => { window.clearTimeout(stall); fn(); };
    xhr.upload.onprogress = (e) => { arm(); if (e.lengthComputable) onProgress?.(Math.round((e.loaded * 100) / e.total)); };
    xhr.onload = done(() => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`cloud storage answered ${xhr.status}`))));
    xhr.onerror = done(() => reject(new Error('network error reaching cloud storage')));
    xhr.onabort = done(() => reject(new Error(signal?.aborted ? 'cancelled' : 'upload stalled')));
    signal?.addEventListener('abort', () => xhr.abort());
    xhr.open('POST', url);
    arm();
    xhr.send(form);
  });
}

export interface UploadOptions {
  module: UploadModule;
  limits: UploadLimits;
  /** The master and field the file is for; the server attaches it nowhere else. */
  target?: { table: string; field: string };
  /** Largest size this field accepts, when tighter than the module's. */
  maxBytes?: number;
  /** Percentage, or null while the size of the remaining work is unknown. */
  onProgress?: (pct: number | null) => void;
  signal?: AbortSignal;
}

export async function uploadFile(original: File, opts: UploadOptions): Promise<FileRef> {
  const { module, limits, onProgress, signal } = opts;
  const problem = refusal(original, limits, Math.min(opts.maxBytes ?? Infinity, limits.max_file_bytes));
  if (problem) throw new UploadError(problem);
  setInFlight(1);
  try {
    const file = await compress(original, limits.image_compression);
    const sha256 = limits.dedup.enabled && file.size <= limits.dedup.max_hash_mb * MB ? await sha256Hex(file) : undefined;
    const init = await api.post<InitResponse>('/files/uploads', {
      module, filename: file.name, size: file.size, mime: file.type || 'application/octet-stream', sha256,
      target_table: opts.target?.table, target_field: opts.target?.field,
    });
    if (init.mode === 'DEDUP') {
      onProgress?.(100);
      return init.file;
    }
    if (init.mode === 'S3') {
      try {
        await postToS3(init.url, init.fields, file, onProgress, signal);
        return await api.post<FileRef>(`/files/uploads/${init.upload_id}/complete`);
      } catch (e) {
        // The server judged the bytes (wrong size, type or checksum): sending them again cannot help.
        if (signal?.aborted || (e instanceof ApiError && e.status < 500)) throw e;
        const { fallback } = await api.post<{ fallback: boolean }>(`/files/uploads/${init.upload_id}/abort`,
          { reason: e instanceof Error ? e.message.slice(0, 300) : 'direct upload failed' });
        if (!fallback) throw new UploadError('Cloud storage is unavailable. Please try again later.');
      }
    }
    onProgress?.(null);
    const form = new FormData();
    form.append('file', file, file.name);
    const ref = await apiUpload<FileRef>(`/files/uploads/${init.upload_id}/local`, form);
    onProgress?.(100);
    return ref;
  } finally {
    setInFlight(-1);
  }
}

/** Open a stored file: inline in a new tab, or as a download. */
export async function openFile(id: string, disposition: 'inline' | 'attachment' = 'inline'): Promise<void> {
  // Popup blockers allow a window opened during the click; the signed URL arrives after an await.
  const tab = disposition === 'inline' ? window.open('', '_blank') : null;
  try {
    const { url } = await api.get<{ url: string }>(`/files/${id}/url`, { disposition });
    if (tab) tab.location.href = url;
    else window.location.assign(url);
  } catch (e) {
    tab?.close();
    throw e;
  }
}

/** Open a feature's file; one saved before app.storage existed has no id yet and comes through the feature's own route. */
export function openStoredFile(fileId: string | null | undefined, legacyPath: string): Promise<void> {
  return fileId ? openFile(fileId) : downloadFile(legacyPath);
}

/** A message for any error the uploader raises, ready to show next to the file. */
export function uploadErrorMessage(e: unknown): string {
  if (e instanceof ApiError) return e.detailMessage;
  if (e instanceof Error) return e.message;
  return 'The upload failed.';
}
